// Copyright © 2019-2026 Focal Systems. All rights reserved.

/** One parsed sheet: the header row plus the data rows, each aligned to `headers` by index. */
export interface SheetData {
  headers: string[];
  rows: string[][];
}

export interface ParsedWorkbook {
  /** Sheet ("page") names, in workbook order — the caller lets the user pick one. */
  sheetNames: string[];
  /** Read a single sheet by name into headers + rows. Unknown names yield an empty sheet. */
  readSheet: (name: string) => SheetData;
}

/**
 * Parse an uploaded spreadsheet (.xlsx / .xls / .csv) into per-sheet header + row data. Values are
 * returned as trimmed strings (dates/numbers use their displayed text); fully-empty rows are dropped.
 * The workbook is parsed once; `readSheet` reads from the in-memory workbook on demand.
 */
export async function parseSpreadsheet(file: File): Promise<ParsedWorkbook> {
  // Lazy-load the (heavy) parser so it code-splits into its own chunk, fetched only on first use.
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  function readSheet(name: string): SheetData {
    const sheet = workbook.Sheets[name];
    if (!sheet) return { headers: [], rows: [] };
    // `header: 1` → array-of-arrays; `raw: false` → formatted display strings; `defval` fills gaps.
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: false,
      defval: "",
      blankrows: false,
    });
    const [headerRow = [], ...body] = matrix;
    const headers = headerRow.map((cell) => String(cell ?? "").trim());
    const rows = body
      .map((row) => headers.map((_, i) => String(row[i] ?? "").trim()))
      .filter((row) => row.some((cell) => cell !== ""));
    return { headers, rows };
  }

  return { sheetNames: workbook.SheetNames, readSheet };
}
